import React, { useEffect, useState } from 'react';
import { IPanVerificationProps } from '../../utils/interface';
import { connect } from 'react-redux';
import { activateLoader, disableLoader } from '@Redux/actions/ui-trigger';
import {
  CHEVRON_LEFT_ICON,
  CLOSE_NEW_ICON,
  ERROR_ICON,
  CIRCLE_CHECK_ICON,
  PAN_CARD_ICON
} from '@Images';
import { CommonButton, StepHeader, ModalFactory, OutlinedTextFields } from '@Components';
import { Field, getFormValues, reduxForm, change } from 'redux-form';
import {
  PAN_VERIFICATION_FORM,
  PAN_VERIFICATION_SCREEN,
  screenSettings,
  totalSteps
} from '../../utils/constants';
import { NF_SCREENS } from '../../root/screenConstants';
import { Col, FormContainer, ParentContainer, Row } from '../../styles';
import { alphanumeric, required } from '@Utils/validation';
import { panLenValidator, panNumberVerification, panNameVerification } from './validation';
import CancelPaymentModal from '../../utils/cancel-payment-modal';
import {
  changeNiumForexScreen,
  initiateNiumForexTransaction,
  storeIsPanVerified,
  storeIsPanNameVerified,
  storeReverifyAadharFlag
} from '@Redux/actions/nium-forex';
import { NF_PAN_EVENT } from '../../../../utils/constants/eventsConstants';
import { FLOW_TYPE } from '@Utils/constants';
import { IStore } from '../../../../store.type';
import { logEvent } from '../../../../utils/events/logUserEvents';
import { LOG_TYPE } from '../../../../utils/events/eventsConstants';

const PanVerificationScreen: React.FC<IPanVerificationProps> = (props) => {
  const stepperData = screenSettings[NF_SCREENS.pan_verification as keyof typeof screenSettings];
  const {
    getAllformVal,
    tempTransactionId,
    changeNiumForexScreen,
    initiateNiumForexTransaction,
    activateLoader,
    disableLoader,
    isPanVerified,
    storeReverifyAadharFlag,
    pan_card,
    name_as_per_aadhar,
    pan_name,
    dispatch,
    isPanNameVerified,
    panVerificationForm
  } = props;
  const [isPanExisted, setIsPanExisted] = useState(false);

  const onPanVerificationSubmit = async () => {
    activateLoader();
    const res = await initiateNiumForexTransaction(tempTransactionId);
    if (res?.success) {
      const data = getAllformVal;
      const ctData = {
        pageName: NF_SCREENS.pan_verification,
        ...data
      };
      logEvent(NF_PAN_EVENT, FLOW_TYPE.TRANSACTION, ctData, LOG_TYPE.CLEVER_TAP);
      changeNiumForexScreen(NF_SCREENS.select_bank);
    }
    disableLoader();
    return;
  };

  const navigateBackUser = async () => {
    await storeReverifyAadharFlag(true);
    changeNiumForexScreen(NF_SCREENS.aadhar_verification);
  };

  const onCancelPayment = async () => {
    // @ts-ignore
    return await ModalFactory.builder?.(
      (onClose: any) => (
        <CancelPaymentModal
          onContinue={() => onClose(true)}
          postPan={false}
          onConfirm={() => {
            changeNiumForexScreen(NF_SCREENS.send_money);
            onClose(true);
          }}
        />
      ),
      'center-modal insta-modal',
      true
    );
  };

  useEffect(() => {
    if (pan_card) {
      dispatch(change(PAN_VERIFICATION_FORM, 'panCardNumber', pan_card));
      setIsPanExisted(true);
    }
    if (name_as_per_aadhar) {
      dispatch(change(PAN_VERIFICATION_FORM, 'aadharName', name_as_per_aadhar));
    }
    if (pan_name) {
      dispatch(change(PAN_VERIFICATION_FORM, 'panName', pan_name));
    }
    // If data is already available then auto panNameVerification should be called to enable CTA
    if (pan_card && name_as_per_aadhar && pan_name && !isPanNameVerified) {
      (async () => {
        const panNumberArg1 = {
          panCardNumber: pan_card,
          panName: pan_name
        };
        const panNumberArg3 = {
          storeIsPanVerified,
          activateLoader,
          isPanVerified
        };
        const panNameArgument1 = {
          panName: pan_name
        };
        const panNameArgument2 = {
          storeIsPanNameVerified: () => dispatch(storeIsPanNameVerified(true)),
          activateLoader
        };
        const panCardValidation = panNumberVerification(panNumberArg1, dispatch, panNumberArg3);
        const panNameValidation = panNameVerification(panNameArgument1, panNameArgument2);

        Promise.all([panCardValidation, panNameValidation]).finally(() => {
          props.disableLoader();
        });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pan_card, name_as_per_aadhar, pan_name]);

  return (
    <ParentContainer padding={[30, 0, 48, 0]}>
      <div className="navigationContainer">
        <img className="backButton" src={CHEVRON_LEFT_ICON} alt="back" onClick={navigateBackUser} />
        <img className="closeButton" src={CLOSE_NEW_ICON} alt="close" onClick={onCancelPayment} />
      </div>
      <StepHeader
        heading={PAN_VERIFICATION_SCREEN.heading}
        headerLogo={PAN_CARD_ICON}
        stepNumber={stepperData?.stepNumber}
        totalSteps={totalSteps}
        subHeading={PAN_VERIFICATION_SCREEN.subHeading}
      />
      <FormContainer marginTop={45}>
        <div>
          <Row padding={[13, 0]}>
            <Col width={450}>
              <Field
                bindToThisObject={getAllformVal?.aadharName}
                parentClassName="form-text-field"
                type="text"
                name="aadharName"
                component={OutlinedTextFields}
                maxWidth="450px"
                validate={[required]}
                disableUnderlineStyling={true}
                showLabel={true}
                placeholder={PAN_VERIFICATION_SCREEN.accountHolderName}
                maxLength={50}
                disabled={true}
              />
            </Col>
          </Row>
          <Row padding={panVerificationForm?.asyncErrors?.panName ? [13, 0, 48, 0] : [13, 0]}>
            <Col width={450}>
              <Field
                bindToThisObject={getAllformVal?.panName || pan_name}
                parentClassName="form-text-field"
                type="text"
                name="panName"
                component={OutlinedTextFields}
                maxWidth="450px"
                validate={[required]}
                disableUnderlineStyling={true}
                showLabel={true}
                placeholder={PAN_VERIFICATION_SCREEN.nameAsPerPanCard}
                errorIcon={ERROR_ICON}
                maxLength={50}
              />
            </Col>
          </Row>
          <Row padding={[13, 0]}>
            <Col width={450}>
              <Field
                bindToThisObject={getAllformVal?.panCardNumber || pan_card}
                parentClassName="form-text-field"
                type="text"
                name="panCardNumber"
                component={OutlinedTextFields}
                maxWidth="450px"
                tooltip={true}
                validate={[required, alphanumeric, panLenValidator]}
                disableUnderlineStyling={true}
                showLabel={true}
                placeholder={PAN_VERIFICATION_SCREEN.panCardNumber}
                errorIcon={ERROR_ICON}
                successIcon={isPanVerified ? CIRCLE_CHECK_ICON : undefined}
                maxLength={10}
                disabled={isPanVerified || isPanExisted}
              />
            </Col>
          </Row>
          <Row padding={[24, 0, 0, 0]}>
            <Col width={450}>
              <CommonButton
                disabled={!isPanNameVerified}
                text={PAN_VERIFICATION_SCREEN.buttonMsg}
                width={450}
                height={46}
                onClick={onPanVerificationSubmit}
              />
            </Col>
          </Row>
        </div>
      </FormContainer>
    </ParentContainer>
  );
};

const createReduxForm = reduxForm({
  form: PAN_VERIFICATION_FORM,
  asyncBlurFields: ['panName', 'panCardNumber'],
  asyncValidate: (values: any, dispatch: any, props: any) => {
    const panCardValidation = values.panCardNumber
      ? panNumberVerification(values, dispatch, props)
      : Promise.resolve();
    const panNameValidation = values.panName
      ? panNameVerification(values, props)
      : Promise.resolve();
    return Promise.all([panCardValidation, panNameValidation]).finally(() => {
      props.disableLoader();
    });
  }
});

function mapStateToProps(reduxState: IStore) {
  const getAllformVal = getFormValues(PAN_VERIFICATION_FORM)(reduxState);
  const {
    rootReducer: {
      niumForexReducer: {
        transactionDetails: { tempTransactionId },
        isPanVerified,
        isPanNameVerified,
        userDetails: { pan_card, name_as_per_aadhar, pan_name }
      }
    },

    form: { panVerificationForm }
  } = reduxState;

  return {
    getAllformVal,
    tempTransactionId,
    isPanVerified,
    isPanNameVerified,
    pan_card,
    name_as_per_aadhar,
    pan_name,
    panVerificationForm
  };
}

const PanVerificationScreenForm = createReduxForm(PanVerificationScreen);

export default connect<any, any, any, IStore>(mapStateToProps, {
  activateLoader,
  disableLoader,
  initiateNiumForexTransaction,
  changeNiumForexScreen,
  storeIsPanVerified,
  storeIsPanNameVerified,
  storeReverifyAadharFlag
  // @ts-ignore
})(PanVerificationScreenForm);
